import { useCallback, useMemo } from "react";

import type { ImpactLeafFactorKey } from "consts";

import type { ScenarioData } from "pages/app/scenario-analysis/types/scenario";
import {
  type ForecastDatum,
  type SnapshotCell,
  type SoilSightOutcomesByScenario,
  type ViewMode,
  buildForecastDataFromSoilSight,
  computeSnapshotColumns,
  forecastKey,
  getAllYears,
  getSnapshotYears,
  getYearData,
} from "pages/app/scenario-analysis/utils/forecast";
import { computeImpactLeafTotals } from "pages/app/scenario-analysis/utils/impactFactors";

/** Factor-leaf totals per scenario id, then per year. */
type LeafTablesByScenario = Record<string, Record<number, Record<ImpactLeafFactorKey, number>>>;

type CMComparisonModelArgs = {
  scenarios: ScenarioData[];
  projectionYears: number;
  /** Precomputed forecast rows; built from the outcomes when left out. */
  forecastData?: ForecastDatum[];
  viewMode: ViewMode;
  soilSightOutcomes: SoilSightOutcomesByScenario | null;
};

/** Everything the comparison table shows, derived from the scenarios and their outcomes for the current view. */
export const useCMComparisonModel = ({ scenarios, projectionYears, forecastData, viewMode, soilSightOutcomes }: CMComparisonModelArgs) => {
  const forecast = useMemo(() => {
    if (forecastData) return forecastData;
    return buildForecastDataFromSoilSight(scenarios, projectionYears, soilSightOutcomes ?? {});
  }, [forecastData, scenarios, projectionYears, soilSightOutcomes]);

  const snapshotYears = useMemo(() => getSnapshotYears(projectionYears), [projectionYears]);
  const allYears = useMemo(() => getAllYears(projectionYears), [projectionYears]);

  const isAllYearsLayout = viewMode !== "cumulative";
  const displayYears = isAllYearsLayout ? allYears : snapshotYears;
  const showTotalCol = viewMode === "annual";

  const snapshotsByScenario: SnapshotCell[][] = useMemo(
    () => scenarios.map((_, idx) => computeSnapshotColumns(forecast, idx, projectionYears, viewMode)),
    [scenarios, forecast, projectionYears, viewMode]
  );

  const baselineIdx = scenarios.findIndex((s) => s.isBaseline);

  const leafByScenarioYear = useMemo(() => {
    const leafTables: LeafTablesByScenario = {};
    scenarios.forEach((scenario) => {
      const yearBreakdowns = soilSightOutcomes?.[scenario.id];
      if (!yearBreakdowns?.length) return;
      leafTables[scenario.id] = {};
      const yearsToCompute = isAllYearsLayout ? allYears : snapshotYears;
      const leafMode = viewMode === "cumulative" ? "cumulative" : viewMode === "yield" ? "yield" : "annual";
      yearsToCompute.forEach((year) => {
        leafTables[scenario.id][year] = computeImpactLeafTotals(yearBreakdowns, year, leafMode);
      });
    });
    return leafTables;
  }, [scenarios, soilSightOutcomes, snapshotYears, allYears, viewMode, isAllYearsLayout]);

  const snapshotCellByScenario = useMemo(() => {
    const cellsByScenario: Record<string, Record<number, SnapshotCell>> = {};
    scenarios.forEach((scenario, idx) => {
      const cells = snapshotsByScenario[idx];
      const byYear: Record<number, SnapshotCell> = {};
      cells.forEach((cell) => {
        byYear[cell.year] = cell;
      });
      cellsByScenario[scenario.id] = byYear;
    });
    return cellsByScenario;
  }, [scenarios, snapshotsByScenario]);

  const getParentValue = useCallback(
    (scenarioIdx: number, year: number): number => {
      const row = getYearData(forecast, year);
      if (viewMode === "annual") return Number(row?.[forecastKey(scenarioIdx, "annual")] ?? 0);
      if (viewMode === "yield") return Number(row?.[forecastKey(scenarioIdx, "yield")] ?? 0);
      return snapshotsByScenario[scenarioIdx]?.find((c) => c.year === year)?.cm ?? 0;
    },
    [forecast, viewMode, snapshotsByScenario]
  );

  const getAnnualTotal = useCallback(
    (scenarioIdx: number): number => {
      const row = getYearData(forecast, projectionYears);
      return Number(row?.[forecastKey(scenarioIdx, "cumulative")] ?? 0);
    },
    [forecast, projectionYears]
  );

  const getBaselineTotal = useCallback(
    (year: number): number => {
      if (baselineIdx < 0) return 0;
      return getParentValue(baselineIdx, year);
    },
    [baselineIdx, getParentValue]
  );

  return {
    forecast,
    allYears,
    displayYears,
    isAllYearsLayout,
    showTotalCol,
    baselineIdx,
    leafByScenarioYear,
    snapshotCellByScenario,
    getParentValue,
    getAnnualTotal,
    getBaselineTotal,
  };
};
